/**
 * This component shows a table of all the specific trigger executions
 * and also show the questions and responses for a specific trigger execution.
 */
import React, { useEffect, useState } from "react";
import useDiscordTriggers from "../DiscordTriggers/useDiscordTriggers";
import _ from "lodash";
import {
  ResponseAggregatesRpc,
  RESPONSE_AGREGATES_TASK_TYPES,
} from "../../lib/discordTypes";
import SurveyCard from "./SurveyCard";
import MultipleSelectCard from "./MultipleSelectCard";
import ScrollableTable from "./ScrollableTable";
import { getSupaClient, getWorkspace } from "../../lib/supabase";
import moment from "moment";
import { usePaywallStateDispatch } from "../Paywall/PaywallContext";
import DateRangePicker from "../DateRangePicker/DateRangePicker";
import Paywall from "../Paywall/Paywall";
import { Alert, Spin } from "antd";

type DropoffRow = {
  question: string;
  name: string;
  id: string;
  dropoff: number;
  step_responses: number;
};

type Props = {
  rootTriggerId: string;
  triggerId: string;
};
const dateFormat = "MM/DD/YY";
const allTimeStart = moment().subtract(50, "y").format(dateFormat);
const allTimeEnd = moment().format(dateFormat);

const ResponseAggregates = ({ rootTriggerId, triggerId }: Props) => {
  const workspace = getWorkspace(getSupaClient());
  const communityId = localStorage.getItem("guild-id");
  const { loadingPlanTier, userPlanTier } = usePaywallStateDispatch();

  // initialize dates to empty string to show all data
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const {
    responseAggregates,
    loadResponseAggregates,
    loadTriggerExecutions,
    triggerExecutions, // trigger executions is not being used right now but will be usefull in next version to get avg time in each step
    loadingResponseAggregates,
  } = useDiscordTriggers();

  const [surveys, setSurveys] = useState<ResponseAggregatesRpc[][]>([]);
  const [questions, setQuestions] = useState<ResponseAggregatesRpc[][]>([]);
  const [totalExecutions, setTotalExecutions] = useState<number>(0);
  const [multipleSelectQuestions, setMultipleSelectQuestions] = useState<
    ResponseAggregatesRpc[][]
  >([]);
  const [steps, setSteps] = useState<DropoffRow[]>([]);

  useEffect(() => {
    // This is a temporal workaround because responseAggregatesRpc doesn't support queries without startDate and endDate, that's why we mock a random old date
    const start = startDate ? startDate : allTimeStart;
    const end = endDate ? endDate : allTimeEnd;
    loadResponseAggregates(rootTriggerId, start, end);
    loadTriggerExecutions(triggerId, communityId || "", workspace, start, end);
  }, [startDate, endDate]);

  const droppoffColumns = [
    {
      key: "step",
      title: "Step",
      width: "50%",
      render: (step: DropoffRow) => (
        <div>
          <p className="m-0 font-bold">{step.name}</p>
          <p className="m-0 text-gray-500 italic">{step.question}</p>
        </div>
      ),
    },
    {
      key: "step_responses",
      title: "Completion count",
      dataIndex: "step_responses",
      width: "25%",
    },
    {
      key: "dropoff",
      title: "Dropoff percent",
      dataIndex: "dropoff",
      width: "25%",
      render: (dropoff: number) => <p className="m-0 ">{dropoff}%</p>,
    },
  ];

  // better naming for displaying task_type to the user
  const getStepName = (type: string) => {
    switch (type) {
      case RESPONSE_AGREGATES_TASK_TYPES.QUESTION:
        return "Open question";
      case RESPONSE_AGREGATES_TASK_TYPES.SURVEY:
        return "Survey";
      case RESPONSE_AGREGATES_TASK_TYPES.MULTIPLE_SELECT:
        return "Multiple select question";
      default:
        return "";
    }
  };

  // drop-off rate calculation
  const getDropoffRate = (
    prevStepResponses: number,
    currentStepResponses: number
  ) => {
    return Math.round(
      ((prevStepResponses - currentStepResponses) / prevStepResponses) * 100
    );
  };

  useEffect(() => {
    if (responseAggregates) {
      // get all steps with responses in the flow, this will be used in the drop off rate table
      const allSteps = _.uniqBy(responseAggregates, "step_id");
      const dropoffData = allSteps
        .map((stp) => {
          const stepDropoffObj = {
            question: stp.question,
            name: getStepName(stp.task_type),
            id: stp.step_id,
            dropoff: getDropoffRate(stp.total_executions, stp.step_responses),
            step_responses: stp.step_responses,
          };
          return stepDropoffObj;
        })
        .sort((a, b) => a.dropoff - b.dropoff);

      setSteps(dropoffData);
      setTotalExecutions(
        typeof allSteps[0]?.total_executions === "number"
          ? allSteps[0].total_executions
          : 0
      );

      // divide responses by task type: survey, questions and multiple select
      const taskTypes = _.groupBy(responseAggregates, "task_type");

      // loop through each task type, rn we have an object where each key is a task type
      for (const key in taskTypes) {
        // devide responses by step using step_id data type will be: ResponseAggregatesRpc[][]
        const responsesGroupedByStep = _.toArray(
          _.groupBy(taskTypes[key], "step_id")
        );
        // Use a switch statement to assign data to the corresponding state variables
        switch (key) {
          case RESPONSE_AGREGATES_TASK_TYPES.QUESTION:
            const responsesWithRepeatedQuestions = responsesGroupedByStep.map(
              (stepRes) =>
                stepRes.map((res) => {
                  return {
                    ...res,
                    answer:
                      res.count > 1
                        ? `(${res.count}) ${res.answer}`
                        : res.answer,
                  };
                })
            );
            setQuestions(responsesWithRepeatedQuestions);
            break;
          case RESPONSE_AGREGATES_TASK_TYPES.MULTIPLE_SELECT:
            const parsedMultipleSelect = responsesGroupedByStep.map((res) => {
              // Count of every single separate option
              const multiSelectResponses: { [key: string]: number } = {};
              res.forEach((item) => {
                const answer = JSON.parse(item.answer);
                answer.forEach((opt: string) => {
                  multiSelectResponses[opt] =
                    typeof multiSelectResponses[opt] === "number"
                      ? multiSelectResponses[opt] + item.count
                      : 1;
                });
              });
              const multiSelectData = _.toPairs(multiSelectResponses).map(
                (pair) => {
                  return {
                    question: res[0].question,
                    step_id: res[0].step_id,
                    task_type: res[0].task_type,
                    step_responses: res[0].step_responses,
                    total_executions: res[0].total_executions,
                    answer: pair[0],
                    count: pair[1],
                  };
                }
              );
              return multiSelectData;
            });
            setMultipleSelectQuestions(parsedMultipleSelect);

            break;
          case RESPONSE_AGREGATES_TASK_TYPES.SURVEY:
            setSurveys(responsesGroupedByStep);
            break;
          default:
            break;
        }
      }
    }
  }, [responseAggregates]);

  return (
    <div className="border-l border-r border-b border-gray-100 -mt-4 rounded-b-lg p-9 flex flex-col gap-4">
      {totalExecutions === 0 && (
        <div>
          <Alert
            message="No executions yet"
            description="You can edit the trigger by clicking Edit on the top. Note: This summary page only shows the published executions, test executions aren't tracked."
            type="info"
            showIcon
          />
        </div>
      )}
      <div className="flex justify-end">
        <DateRangePicker
          endDate={endDate}
          setEndDate={setEndDate}
          startDate={startDate}
          setStartDate={setStartDate}
          isIndependentDateSelector
          showLoadingAlert={false}
          displayShowAllOption
          customOption={{ disabled: false }}
          rangeOfSelector={{
            range: "day",
            selectorDefaultOptions: [
              { value: 7, disabled: false },
              {
                value: 30,
                disabled: false,
              },
            ],
          }}
        />
      </div>
      <div>
        <div className="w-full mb-5">
          <p className="font-bold text-left m-0 text-center text-lg text-gray-500">
            Total executions
          </p>
          <p className="font-bold text-center m-0 text-5xl">
            {!loadingResponseAggregates && totalExecutions}
            {loadingResponseAggregates && <Spin size="small" />}
          </p>
        </div>
      </div>
      <div>
        <p className="text-lg font-bold text-left">Drop off rate</p>
        <Paywall
          free={
            <ScrollableTable
              data={steps}
              columns={droppoffColumns}
              fullHeightAllowed
            />
          }
          pro={
            <ScrollableTable
              data={steps}
              columns={droppoffColumns}
              paywall={false}
              fullHeightAllowed
            />
          }
          business={
            <ScrollableTable
              data={steps}
              columns={droppoffColumns}
              paywall={false}
              fullHeightAllowed
            />
          }
          tier={userPlanTier}
          loading={loadingPlanTier}
        />
      </div>
      <div className="flex flex-col mt-5">
        <p className="text-lg font-bold m-0 text-left mb-3">Survey questions</p>
        <div className="flex gap-5 flex-wrap">
          {surveys.map((surveyData) => (
            <SurveyCard data={surveyData} key={surveyData[0].step_id} />
          ))}
        </div>
      </div>
      <div className="flex flex-col mt-5">
        <p className="text-lg font-bold m-0 text-left mb-3">
          Multiple select questions
        </p>
        <div className="flex gap-5">
          {multipleSelectQuestions.map((multipleSelectQuestionData) => (
            <MultipleSelectCard
              data={multipleSelectQuestionData}
              key={multipleSelectQuestionData[0].step_id}
            />
          ))}
        </div>
      </div>
      <div className="mt-5">
        <p className="text-lg font-bold text-left">User response</p>
        <div className="flex flex-wrap gap-5">
          {questions.map((questionsData) => (
            <ScrollableTable
              data={questionsData}
              columns={[
                {
                  key: "answer",
                  // title: `${questionsData[0].step_responses} responses - ${questionsData[0].question}`,
                  title: (
                    <div>
                      <p className="m-0">{questionsData[0].question}</p>
                      <p className="m-0 text-xs font-light">
                        {questionsData[0].step_responses} responses
                      </p>
                    </div>
                  ),
                  dataIndex: "answer",
                  width: "100%",
                },
              ]}
              fullWidth={false}
              paywall={false}
              key={questionsData[0].step_id}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResponseAggregates;
